import type { ReactNode } from "react"

import { Card, CardContent } from "@/components/ui/card"
import { cn } from "@/lib/utils"

export type GlowVariant =
  | "purple"
  | "turquoise"
  | "sunset"
  | "mystic"
  | "ember"
  | "mystical-orange"
  | "none"

interface GlowEventCardProps {
  title: string
  description?: string | null
  badge?: ReactNode
  scheduleText?: string | null
  meta?: ReactNode
  metaClassName?: string
  actions?: ReactNode
  imageUrl?: string | null
  imageAlt?: string
  reversed?: boolean
  glowVariant?: GlowVariant
  className?: string
  /** Extra classes for the image column wrapper (e.g. spacing below image on mobile). */
  imageContainerClassName?: string
  glowOpacity?: number
}

const glowVariants: Record<
  Exclude<GlowVariant, "none">,
  { primary: string; secondary: string }
> = {
  purple: {
    primary: "147 51 234",
    secondary: "99 102 241",
  },
  turquoise: {
    primary: "20 184 166",
    secondary: "6 182 212",
  },
  sunset: {
    primary: "249 115 22",
    secondary: "244 63 94",
  },
  mystic: {
    primary: "59 130 246",
    secondary: "14 165 233",
  },
  ember: {
    primary: "249 115 22",
    secondary: "251 191 36",
  },
  "mystical-orange": {
    primary: "249 115 22",
    secondary: "234 88 12",
  },
}

function glowBackground(glow: { primary: string; secondary: string }) {
  return [
    `radial-gradient(ellipse 68% 78% at 50% 50%, rgb(${glow.primary} / 0.58) 0%, rgb(${glow.secondary} / 0.3) 48%, transparent 82%)`,
    `radial-gradient(ellipse 42% 48% at 78% 20%, rgb(${glow.secondary} / 0.34) 0%, transparent 76%)`,
  ].join(", ")
}

export default function GlowEventCard({
  title,
  description,
  badge,
  scheduleText,
  meta,
  metaClassName,
  actions,
  imageUrl,
  imageAlt,
  reversed = false,
  glowVariant = "purple",
  className,
  imageContainerClassName,
  glowOpacity = 1,
}: GlowEventCardProps) {
  const glow = glowVariant === "none" ? null : glowVariants[glowVariant]
  const contentLayoutClass = reversed ? "lg:flex-row-reverse" : "lg:flex-row"
  const cardPaddingClass = reversed ? "lg:pr-8 !pl-0" : "lg:pl-8 !pr-0"
  const contentPaddingClass = reversed ? "lg:px-8 lg:pl-0" : "lg:px-8 lg:pr-0"
  const contentSpacingClass = reversed ? "lg:pl-12" : ""
  const imageContainerClass =
    "shrink-0 w-full lg:min-w-[400px] lg:w-64 xl:w-[30%] aspect-auto overflow-hidden p-0 lg:p-4 lg:pl-8"
  const imageWrapperClass =
    "w-full overflow-hidden rounded-t-[15px] rounded-b-none shadow-none lg:rounded-[15px] lg:shadow-2xl"
  const imageClass = "h-auto w-full object-contain"

  return (
    <div className="relative isolate">
      {glow && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 -z-10 rounded-3xl"
          style={{
            backgroundImage: glowBackground(glow),
            filter: "blur(48px)",
            opacity: glowOpacity,
          }}
        />
      )}

      <Card
        className={`relative z-50 overflow-hidden border border-[#ffffff0f] bg-[#09090994] text-white ${cardPaddingClass} !p-0 lg:!py-0 ${className ?? ""}`}
      >
        <CardContent
          className={`relative px-0 pt-0 pb-0 lg:p-0 ${contentPaddingClass}`}
        >
          <div
            className={`flex flex-col-reverse ${contentLayoutClass} gap-0 lg:items-stretch lg:gap-6`}
          >
            <div
              className={`flex-1 space-y-4 ${contentSpacingClass} px-6 pt-0 pb-8 lg:px-0 lg:py-12`}
            >
              {(badge != null || scheduleText != null) && (
                <div className="flex items-center gap-3">
                  {badge}
                  {scheduleText && (
                    <span className="text-sm text-white/80">
                      {scheduleText}
                    </span>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <h2 className="text-2xl font-bold tracking-tight md:text-3xl">
                  {title}
                </h2>
                {description && (
                  <p className="line-clamp-2 text-sm text-white/80 md:text-base">
                    {description}
                  </p>
                )}
              </div>

              {meta && (
                <div
                  className={
                    metaClassName ??
                    "flex flex-wrap items-center gap-4 text-sm text-white/80"
                  }
                >
                  {meta}
                </div>
              )}

              {actions && (
                <div className="flex flex-wrap gap-3 pt-2">{actions}</div>
              )}
            </div>

            {imageUrl && (
              <div className={cn(imageContainerClass, imageContainerClassName)}>
                <div className={imageWrapperClass}>
                  <img
                    src={imageUrl}
                    alt={imageAlt ?? title}
                    className={imageClass}
                  />
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
